// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-03:
// Independent learning cluster article: preparing for law seminars and
// tutorials. Wave-1 research found acute, university-acknowledged pain
// (Bristol: under-prepared seminars are stressful and participation
// becomes impossible) with near-zero dedicated public coverage. Marking
// and participation practices vary by university and are framed as
// "check your module guide" throughout.

import type { ResourceSection, ResourceSourceGroup } from "./types";

export const preparingForLawSeminarsSections: ResourceSection[] = [
  {
    id: "why-seminars-feel-exposing",
    heading: "Why seminars feel so exposing",
    paragraphs: [
      "The preparation method works across UK courses. Examples of case terminology and the linked court sources mainly concern England and Wales; use the terminology and authorities required by your module if you study Scots or Northern Irish law. Seminar format and participation marking are set by your course.",
      "A lecture lets you hide. A seminar does not. Somewhere between eight and twenty people sit around a table, the tutor asks a question about the reading, and the silence that follows has your name in it. Almost every law student knows the specific dread of being called on for a case they only skimmed, and the equally specific misery of sitting through a discussion they cannot follow because the preparation did not happen.",
      "Law teachers say this plainly: preparing properly is what makes the session useful at all, walking in under-prepared tends to be stressful, following the discussion becomes genuinely difficult, and meaningful participation becomes close to impossible. The fix is not courage. It is preparation of a specific, learnable kind, and it takes less time than the guilt-fuelled version most students attempt.",
    ],
    links: [
      {
        label: "University of Bristol Law School blog: how do I prepare for class?",
        href: "https://legalresearch.blogs.bris.ac.uk/2021/10/study-skill-series-how-do-i-prepare-for-class/",
      },
      {
        label: "Goldsmiths, University of London: making the most of lectures and seminars",
        href: "https://libguides.gold.ac.uk/aso/lecturesandseminars",
      },
    ],
  },
  {
    id: "what-seminars-are-for",
    heading: "What seminars are actually for",
    paragraphs: [
      "A seminar is not a small lecture. Nobody is going to re-teach the material, and reading your notes aloud is not the exercise. The seminar is where you test your understanding against other minds: you commit to a position, hear it challenged, defend it or update it, and watch stronger and weaker arguments collide in real time. It is the weekly, low-stakes version of exactly what exams and moots assess.",
      "That reframe changes how preparation feels. You are not preparing to avoid embarrassment; you are preparing to run experiments on your own understanding while mistakes are still free. The student who says something wrong in week four and gets corrected has banked a lesson the silent student has not. Whether participation formally contributes to marks varies between universities and modules, so check your module guide, but the learning value of speaking is the same either way.",
    ],
  },
  {
    id: "what-prepared-actually-means",
    heading: "What prepared actually means",
    paragraphs: [
      "Prepared does not mean you read everything and remember all of it. Nobody does. Prepared means you arrive with a small, specific set of artefacts, and they fit on one or two pages:",
    ],
    bullets: [
      "The seminar questions, read before the reading, so you knew what you were reading for.",
      "A drafted position on each question, even a tentative one, in a few bullet points.",
      "Half-page briefs of the central cases: facts in two sentences, the ratio in one, why it is on this list.",
      "One thing you genuinely did not understand, written as a question you could ask.",
      "One opinion you hold about the topic that you could defend for thirty seconds.",
    ],
    callout: {
      title: "The seminar sheet comes first",
      body: "The single highest-value habit: read the seminar questions before you touch the reading list. Reading with the questions in hand turns passive coverage into a targeted search, and it usually cuts preparation time rather than adding to it.",
    },
  },
  {
    id: "the-preparation-sequence",
    heading: "A realistic preparation sequence",
    paragraphs: [
      "Here is the sequence that produces those artefacts without consuming your week. First, read the seminar sheet and turn each question into what it is really asking: which doctrine, which tension, which case. Second, triage the reading list against those questions: the items the questions depend on get proper attention, the rest get a purposeful skim. Third, brief the central cases using the same half-page template you use everywhere else. Fourth, draft bullet answers to each question, including the point where your answer runs out. Fifth, write down your one honest question.",
      "Notice what is missing: copying out chapters, transcribing lectures, and reading every item start to finish. Those feel like work and mostly are not. If the reading load still will not fit, our case reading guide's layered method and triage rules are built for exactly this problem.",
    ],
  },
  {
    id: "speaking-up",
    heading: "Speaking up when everyone seems smarter",
    paragraphs: [
      "The fear of saying something wrong in front of the group is close to universal, and it feeds on a reliable illusion: everyone else seems to understand. They do not. Law cohorts are simply skilled at looking composed, and the confident speaker who answers first is frequently wrong in instructive ways. The tutor knows this; it is why they teach by discussion.",
      "If speaking feels hard, you are in well-documented company: university wellbeing services publish guidance specifically for finding your voice in seminars, and they name silence and sitting at the back as the avoidance habits that keep the fear alive. Engineer easy entries rather than waiting for confidence. Say something early, because the pressure of not-yet-having-spoken compounds by the minute. Asking a genuine question counts fully as participation, and so does building on someone else's answer with an extra authority or a counterexample. Your drafted bullets from preparation are your safety net: you are never improvising from nothing, you are reading the room and choosing which prepared thought to offer.",
    ],
    links: [
      {
        label: "Goldsmiths, University of London: finding your voice in groups and seminars",
        href: "https://www.gold.ac.uk/students/wellbeing/guides/finding-your-voice/",
      },
    ],
  },
  {
    id: "the-week-you-did-not-prepare",
    heading: "The week you did not prepare",
    paragraphs: [
      "It will happen: a deadline collision, an application week, an illness, a bad stretch. The wrong response is skipping the seminar, which converts one bad week into a knowledge gap plus a habit. The right response is honest triage: twenty minutes with the seminar sheet, the lecture notes for that topic, and one central case, then attend and listen hard, contribute where you safely can, and mark the topic for repair.",
      "Then actually repair it. A missed topic in a cumulative subject does not stay the same size; it grows quietly until revision, when it is expensive. A one-line note in your weekly review, and a planned hour the following week, keeps the debt small.",
    ],
  },
  {
    id: "after-the-seminar",
    heading: "The ten minutes after the seminar",
    paragraphs: [
      "The most under-used study window in the week is the ten minutes straight after a seminar, while the discussion is still warm. Fix your notes where the discussion corrected you. Write down the point the tutor kept steering everyone toward, because that emphasis is rarely accidental. Note the question you still cannot answer, and whether anyone answered yours.",
      "Done weekly, this turns seminars into a self-updating revision resource: by the end of term you hold a set of documents that record not just the law, but where your understanding wobbled and how it was fixed. That is precisely the material exam preparation wants, and it costs ten minutes while everyone else is packing up.",
    ],
  },
  {
    id: "common-mistakes",
    heading: "Common mistakes, so you can skip them",
    paragraphs: ["Most seminar pain comes from a short list of avoidable patterns:"],
    bullets: [
      "Reading the list before the questions, so nothing had a purpose.",
      "Trying to read everything fully, running out of time, and arriving with nothing usable.",
      "Preparing notes but no positions, then having nothing to say when asked what you think.",
      "Staying silent all term, so the first time you argue law aloud is in an assessment.",
      "Treating a wrong answer as a catastrophe rather than the cheapest correction you will ever get.",
      "Skipping the week you did not prepare, and letting one gap become two.",
      "Leaving the seminar without fixing your notes while the corrections were still fresh.",
    ],
  },
  {
    id: "where-durmah-fits",
    heading: "Where Durmah fits",
    paragraphs: [
      "Durmah is a useful rehearsal room for seminar preparation: test yourself on the doctrines the seminar sheet points at, talk through your drafted position out loud before you have to defend it in company, and check your understanding of the cases you briefed. Speaking law before the seminar makes speaking law in the seminar noticeably easier.",
      "What Durmah will not do is read the list or hold the opinion for you. The seminar is where your own reasoning gets tested and strengthened, and it only works on reasoning you actually own. The preparation stays yours; the practice partner is optional.",
    ],
  },
];

export const preparingForLawSeminarsSources: ResourceSourceGroup[] = [
  {
    topic: "University study-skills guidance",
    sources: [
      {
        label: "University of Bristol Law School blog: how do I prepare for class?",
        href: "https://legalresearch.blogs.bris.ac.uk/2021/10/study-skill-series-how-do-i-prepare-for-class/",
      },
      {
        label: "Goldsmiths: making the most of lectures and seminars",
        href: "https://libguides.gold.ac.uk/aso/lecturesandseminars",
      },
      {
        label: "Goldsmiths wellbeing: finding your voice in groups and seminars",
        href: "https://www.gold.ac.uk/students/wellbeing/guides/finding-your-voice/",
      },
    ],
  },
  {
    topic: "Primary sources for your briefs",
    sources: [
      { label: "Find Case Law: England and Wales decisions plus UK Supreme Court and Privy Council", href: "https://caselaw.nationalarchives.gov.uk/" },
      { label: "BAILII: free UK and Irish case law", href: "https://www.bailii.org/" },
    ],
  },
];
